import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Check, QrCode } from "lucide-react";
import { toast } from "sonner";

const BookingQRDialog = ({ open, onOpenChange, bookingId, bookingRef, laneNumbers, bookingUnit }) => {
  const [copied, setCopied] = useState(false);
  const url =
    typeof window !== "undefined" && bookingId
      ? `${window.location.origin}/checkin/${bookingId}`
      : "";

  const laneLabel = laneNumbers && laneNumbers.length
    ? bookingUnit === "box"
      ? `Box · lanes ${laneNumbers[0]}–${laneNumbers[laneNumbers.length - 1]}`
      : `Lane #${laneNumbers[0]}`
    : null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied — share with your group.");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy link.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-spike-elevated border border-spike-blue/40 text-spike-text max-w-md p-0"
        data-testid="booking-qr-dialog"
      >
        <div className="bg-spike-elevated/60 p-7 border-b border-white/10">
          <QrCode className="w-7 h-7 text-spike-blue-light" strokeWidth={1.5} />
          <DialogHeader className="mt-4 text-left">
            <DialogTitle className="font-orbitron font-black uppercase text-2xl text-spike-text tracking-tight">
              Check-in QR
            </DialogTitle>
            <DialogDescription className="font-exo text-spike-text-muted text-sm mt-2">
              Everyone in your group scans this to check in. They'll sign in with
              Google and sign the liability waiver — one-time per person.
            </DialogDescription>
          </DialogHeader>
          {bookingRef && (
            <div className="mt-3 font-orbitron text-[0.65rem] uppercase tracking-[0.3em] text-spike-blue-light flex flex-wrap items-center gap-2">
              <span>Booking · #{bookingRef}</span>
              {laneLabel && (
                <span className="inline-flex items-center px-2 py-0.5 border border-spike-blue/40 bg-spike-blue/10 text-white" data-testid="qr-lane-label">
                  {laneLabel}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="p-7">
          <div className="bg-spike-elevated p-6 mx-auto w-fit" data-testid="qr-canvas">
            {url && (
              <QRCodeSVG
                value={url}
                size={232}
                level="M"
                includeMargin={false}
                fgColor="#0A101C"
                bgColor="#ffffff"
              />
            )}
          </div>
          <div className="mt-6">
            <label className="block text-[0.6rem] font-bold uppercase tracking-[0.25em] text-spike-text-muted mb-2 font-orbitron">
              Direct link
            </label>
            <div className="flex items-stretch border border-white/10">
              <input
                readOnly
                value={url}
                data-testid="qr-link-input"
                className="flex-1 bg-spike-elevated px-3 py-3 text-xs font-exo text-spike-text truncate focus:outline-none"
              />
              <button
                type="button"
                data-testid="qr-copy-btn"
                onClick={copy}
                className="px-4 bg-spike-blue hover:bg-spike-blue-hover text-white font-orbitron text-[0.65rem] uppercase tracking-[0.25em] flex items-center gap-1.5"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BookingQRDialog;
